import React from 'react';
import Button from '../../../Button/Button';
import EmptyState from '../../../EmptyState/EmptyState';
const emptyStateFor = 'am-v2-space-assets-empty-state';

const SuggestionsEmptyState = {
  heading: <div data-test-id={`${emptyStateFor}-heading`}>Asset Suggestion Issue</div>,
  description: (
    <div data-test-id={`${emptyStateFor}-description`}>
      We couldn’t complete suggesting asset for this entry You can retry to continue, or switch to All Assets view from the top dropdown to explore other options.
    </div>),
  forPage: 'emptyStateV2',
  moduleIcon: 'NoAssetsSuggestionsDataState',
  actionButtonTestId: `${emptyStateFor}-retry-btn`,
  actionType: 'RETRY',
  actionButtonText: 'Retry',
}

export const EmptyFolderObj: any = {
  heading: <div data-test-id={`${emptyStateFor}-heading`}>This folder is empty</div>,
  description: (
    <div className="mr-5" data-test-id="cs-asset-empty-state-description">
      There are no assets here.
    </div>
  ),
  forPage: 'emptyStateV2',
  moduleIcon: 'NoDataFolderState',
  actionButtonText: 'Go Back',
  actionButtonTestId: `${emptyStateFor}-go-back-btn`,
  actionType: 'GO_BACK'
};

export const MoveTargetEmptyFolder: any = {
  heading: <div data-test-id={`${emptyStateFor}-heading`}>Move assets into this folder.</div>,
  description: (
    <div data-test-id="cs-asset-empty-state-description">
      Move assets into this folder or create a new folder.
    </div>
  ),
  forPage: 'emptyStateV2',
  moduleIcon: 'NoDataFolderState',
  actionType: 'NONE'
};

export const MoveTargetNewFolder: any = {
  heading: <div data-test-id={`${emptyStateFor}-heading`}>Your New Folder Is Ready — Move Assets To Get Started</div>,
  description: (
    <div data-test-id="cs-asset-empty-state-description">
      There are no subfolders here, but you can move your assets directly into this folder.
    </div>
  ),
  forPage: 'emptyStateV2',
  moduleIcon: 'NoDataFolderState',
  actionType: 'NONE'
};

export const UploadTargetEmptyFolder: any = {
  heading: <div data-test-id={`${emptyStateFor}-heading`}>Upload Assets To This Folder</div>,
  description: (
    <div data-test-id="cs-asset-empty-state-description">
      Drag and drop files here or use the Upload Assets button below to add files from your library.
    </div>
  ),
  forPage: 'emptyStateV2',
  moduleIcon: 'NoDataFolderState',
  actionType: 'NONE'
};

export const UploadTargetNewFolder: any = {
  heading: <div data-test-id={`${emptyStateFor}-heading`}>Your New Folder Is Ready — Add Assets To Get Started</div>,
  description: (
    <div data-test-id="cs-asset-empty-state-description">
      Drag and drop files here or use the Upload Assets button below to add files from your library.
    </div>
  ),
  forPage: 'emptyStateV2',
  moduleIcon: 'NoDataFolderState',
  actionType: 'NONE'
};

export const NoResultEmptyObj: any = {
  heading: <div data-test-id={`${emptyStateFor}-heading`}>No Assets Found</div>,
  description: (
    <div data-test-id={`${emptyStateFor}-description`}>
      No matches found for your search or filters. Try adjusting your search keywords, filters, or clearing them to
      see assets.
    </div>
  ),
  forPage: 'emptyStateV2',
  moduleIcon: 'NoDataEmptyState',
  actionButtonText: 'Clear All',
  actionButtonTestId: `${emptyStateFor}-clear-all-btn`,
  actionType: 'CLEAR_FILTERS'
};

export const NoAssetsFound: any = {
  heading: <div data-test-id={`${emptyStateFor}-heading`}>No Assets Found</div>,
  description: (<div data-test-id={`${emptyStateFor}-description`}>
    Sorry, something went wrong. Please try again.
  </div>),
  forPage: 'emptyStateV2',
  moduleIcon: 'NoDataEmptyState',
  actionButtonText: 'Retry',
  actionButtonTestId: `${emptyStateFor}-retry-btn`,
  actionType: 'RETRY'
};

export const getEmptyStateWithActions = (
  emptyObj: any,
  handlers: {
    handleGoBack?: () => void;
    handleRetryFetch?: () => void;
    handleClearFilters?: () => void;
  }
): any => {
  let actionButton: React.ReactNode = null;

  if (emptyObj.actionType === 'GO_BACK' && handlers.handleGoBack) {
    actionButton = (
      <Button
        buttonType="tertiary"
        testId={emptyObj.actionButtonTestId}
        onClick={handlers.handleGoBack}>
        {emptyObj.actionButtonText}
      </Button>
    );
  } else if (emptyObj.actionType === 'RETRY' && handlers.handleRetryFetch) {
    actionButton = (
      <Button
        buttonType="tertiary"
        testId={emptyObj.actionButtonTestId}
        onClick={handlers.handleRetryFetch}>
        {emptyObj.actionButtonText}
      </Button>
    );
  } else if (emptyObj.actionType === 'CLEAR_FILTERS' && handlers.handleClearFilters) {
    actionButton = (
      <Button
        buttonType="tertiary"
        testId={emptyObj.actionButtonTestId}
        onClick={handlers.handleClearFilters}>
        {emptyObj.actionButtonText}
      </Button>
    );
  }

  return {
    ...emptyObj,
    actions: actionButton
  };
};

export const SpaceAssetsEmptyState = ({
  searchApplied,
  setShouldClearSearchFilters,
  handleGoBack,
  handleRetryFetch,
  isAPIError,
  isSuggestions,
  isResponsive = false,
  isMoveTarget = false,
  isUploadTarget = false,
  isNewlyCreatedFolder = false,
}: {
  searchApplied: boolean;
  setShouldClearSearchFilters: (shouldClearSearchFilters: boolean) => void;
  handleGoBack: () => void;
  handleRetryFetch: () => void;
  isAPIError: boolean;
  isSuggestions?: boolean;
  isResponsive?: boolean;
  isMoveTarget?: boolean;
  isUploadTarget?: boolean;
  isNewlyCreatedFolder?: boolean;
}): JSX.Element => {
  let emptyObj = searchApplied ? NoResultEmptyObj : EmptyFolderObj;
  isAPIError && (emptyObj = NoAssetsFound);
  isSuggestions && (emptyObj = SuggestionsEmptyState);

  // Override empty state for folder picker modes
  if (isMoveTarget && !searchApplied && !isAPIError && !isSuggestions) {
    if (isUploadTarget) {
      emptyObj = isNewlyCreatedFolder ? UploadTargetNewFolder : UploadTargetEmptyFolder;
    } else {
      emptyObj = isNewlyCreatedFolder ? MoveTargetNewFolder : MoveTargetEmptyFolder;
    }
  }

  const handlers = {
    handleGoBack,
    handleRetryFetch,
    handleClearFilters: (): void => setShouldClearSearchFilters(true)
  };

  const emptyObjWithActions = getEmptyStateWithActions(emptyObj, handlers);

  const Heading: React.FC = () => emptyObjWithActions.heading;
  const Description: React.FC = () => emptyObjWithActions.description;

  return (
    <EmptyState
      testId={`${emptyStateFor}-${isAPIError ? 'error' : searchApplied ? 'search' : 'suggestions'}`}
      forPage={emptyObjWithActions.forPage}
      heading={<Heading />}
      description={<Description />}
      moduleIcon={emptyObjWithActions.moduleIcon}
      actions={emptyObjWithActions.actions}
      className={`${isResponsive ? 'EmptyState--resp_screen' : ''}`}
    />
  );
};